import { AgriField, AgriFieldDetail, AgriFieldsGet, Crop, SelectAgriFieldAndPlantingInWorkRecord } from '@/types/Component';
import moment from 'moment';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import Modal from "@/assets/common/Modal/Modal.vue";
import { toCamel } from 'snake-camel';

type PreSelectItem = {
    agriFieldId: string,
    plantingId: string | null,
}

type SelectPlanting = {
    id: string | null,
    name: string,
    cropFamicId: string | null
}

type SubPlanting = {
    id: string | null, // 作付のID
    name: string,
    cropFamicId: string;
    crop: Crop,
    cultivationPlan: {
        workStart: Date | null, // 作業期間のスタート
        workEnd: Date | null // 作業期間のエンド
    },
    agriFields?: Array<AgriField>,
    workStartDate?: Date, // 作業期間のスタート
    workEndDate?: Date // 作業期間のエンド
    isHidden?: boolean // 非表示フラグ, true: 非表示
}

@Component({
    components: {Modal}
})
export default class SelectFrom extends Vue {
    @Prop()
    private value?: boolean;

    @Emit('input')
    input(value: boolean): void {
        value
    }

    @Prop()
    private preSelectItem?: PreSelectItem;

    @Prop()
    private workDate?: Date;

    @Prop()
    private showSelectFrom?: boolean;

    @Emit('selectedItem')
    selectedItem(): SelectAgriFieldAndPlantingInWorkRecord {
        this.input(false)
        return { agriField: this.preSelectedField, planting: this.selectedPlanting }
    }

    private outOfTerm = false;
    private searchWord = '';

    private agriFields: Array<AgriField> = []
    private filteredAgriFields: Array<AgriField> = []
    private displayAgriFields: Array<AgriField> = []

    private selectedPlanting: SelectPlanting = { id: '', name: '', cropFamicId: '' };
    private selectedField = { id: '', name: '', plantedArea: 0 };
    private preSelectedField = {id: '', name: '', plantedArea: 0};
    private plantings: SubPlanting[] = []
    private filteredPlantings: SubPlanting[] = []
    private displayPlantings: SubPlanting[] = []
    private completeDisabled = true
    private sortByPlantingFlag = 1
    private sortByTermFlag = 1

    @Watch('value')
    async whenOpen(): Promise<void> {
        if(this.value) {
            this.searchWord = ''
            const res = toCamel(await this.$apiClient.doRequest('get', '/agri_fields')) as AgriFieldsGet
            this.agriFields = res.agriFields
            this.filteredAgriFields = res.agriFields.filter((af: AgriField) => {
                return this.isWithinTheTerm(af.displayStart, af.displayEnd)
            })

            const {agriFieldId, plantingId} = this.preSelectItem ? this.preSelectItem : {agriFieldId: '', plantingId: ''}
            const agriField = this.agriFields.find(af => af.id === agriFieldId)

            if(agriField) {
                this.selectedField = this.preSelectedField = {id: agriField.id, name: agriField.name, plantedArea: agriField.plantedArea}
                await this.getPlantings(agriField.id)
                const planting = this.plantings.find(p => p.id === plantingId)
                if(plantingId === null) {
                    this.selectPlanting(null, '作付を指定しない', null)
                    // 期間内判定
                    this.outOfTerm = !this.filteredAgriFields.some(af => af.id === agriField.id)
                } else if(planting) {
                    this.selectPlanting(planting.id, planting.name, planting.cropFamicId)
                    // 期間内判定
                    this.outOfTerm = !(this.filteredAgriFields.some(af => af.id === agriField.id)
                        && this.filteredPlantings.some(p => p.id === planting.id))
                } else {
                    this.selectedPlanting = { id: '', name: '', cropFamicId: '' }
                    this.outOfTerm = !this.filteredAgriFields.some(af => af.id === agriField.id)
                }
            } else {
                const initField = this.filteredAgriFields[0]
                initField ? await this.selectField(initField.id, initField.name, initField.plantedArea)
                    : this.resetSelectItem()
                this.outOfTerm = false
            }
            this.filteringItems()
        } else {
            this.resetSelectItem()
        }
    }

    // 選択状態と作付表示の初期化
    private resetSelectItem() {
        this.selectedField = { id: '', name: '', plantedArea: 0 }
        this.selectedPlanting = { id: '', name: '', cropFamicId: '' }
        this.outOfTerm = false
        this.displayPlantings = []
        this.completeDisabled = true
    }

    private formatWorkDate(date: Date) {
        return moment(date).format('YYYY年M月D日')
    }

    private async getPlantings(agriFieldId: string) {
        const plantings = (toCamel(await this.$apiClient.doRequest('get', '/agri_fields/' + agriFieldId)) as AgriFieldDetail).plantings as SubPlanting[] || []
        this.plantings = plantings.filter(p => !p.isHidden)

        this.filteredPlantings = this.plantings.filter(p => {
            return this.isWithinTheTerm(p.cultivationPlan.workStart, p.cultivationPlan.workEnd)
        })
    }

    async selectField(id: string, name: string, plantedArea: number): Promise<void> {
        this.selectedField = { id, name, plantedArea }
        await this.getPlantings(id)
        this.filteringPlantings()
    }

    private selectPlanting(id: string | null, name: string, cropFamicId: string | null) {
        this.preSelectedField = this.selectedField
        this.selectedPlanting = { id, name, cropFamicId }
        this.completeDisabled = false
    }

    private checkedPlanting(id: string) {
        return this.selectedPlanting.id === id && this.preSelectedField.id === this.selectedField.id
        ? 'checked'
        : ''
    }

    private isWithinTheTerm(start: Date | null | undefined, end: Date | null | undefined) {
        const workDate = moment(this.workDate)
        const startDate = moment(start)
        const endDate = moment(end)
        return (!startDate.isValid() || workDate.isSameOrAfter(startDate)) && (!endDate.isValid() || workDate.isSameOrBefore(endDate))
    }

    private filteringItems() {
        this.displayAgriFields = this.outOfTerm ? this.agriFields : this.filteredAgriFields
        this.displayAgriFields = this.displayAgriFields.filter(af => (
            af.name.indexOf(this.searchWord) > -1
        ))
        if((this.displayAgriFields.length<=0 && this.searchWord.length>0)){
            this.resetSelectItem()
        }else if((this.displayAgriFields.length>0 && this.searchWord.length>0)){
            const isPlantingSelected = this.displayAgriFields.some(agriField=>{
                return agriField.id == this.selectedField.id;
            })
            if(!isPlantingSelected){
                this.resetSelectItem()
            }
        }
        this.filteringPlantings()
        if (!this.displayAgriFields.find(agriField => agriField.id === this.selectedField.id)) {
            // 期間外の圃場を選択している際に「期間外も表示」のチェックを外した場合に圃場を未選択状態にする
            this.selectedField = { id: '', name: '', plantedArea: 0 }
            this.displayPlantings = []
        }
    }

    private filteringPlantings() {
        this.displayPlantings = this.outOfTerm ? this.plantings : this.filteredPlantings
    }

    get isModalVisible(): boolean {
        return this.value ? this.value : false
    }

    set isModalVisible(value: boolean) {
        this.input(value)
    }

    private sortByPlanting() {
        const notSpecified = '作付を指定しない'
        this.displayPlantings.sort((a, b) => a.name != notSpecified && b.name != notSpecified
            ? a.name > b.name
                ? this.sortByPlantingFlag
                : -this.sortByPlantingFlag
            : -1
        )
        this.sortByPlantingFlag = -this.sortByPlantingFlag
    }

    private sortByTerm() {
        this.displayPlantings.sort((a, b) =>
            moment(a.cultivationPlan.workStart).isValid() && moment(b.cultivationPlan.workStart).isValid()
                ? moment(a.cultivationPlan.workStart).isSameOrBefore(moment(b.cultivationPlan.workStart))
                    ? this.sortByTermFlag
                    : -this.sortByTermFlag
                : 1
        )
        this.sortByTermFlag = -this.sortByTermFlag
    }
}
